import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  Badge,
  Button,
  Filter,
  Grid,
  IconButton,
  Input,
  List,
  Search,
  Segmented,
  Select,
  Sheet,
  X,
} from '@/components/ui';
import { useMarket } from '@/providers/market';
import { cn } from '@/lib/cn';
import { compact } from '@/lib/format';

export type SortKey =
  | 'trending'
  | 'newest'
  | 'volume'
  | 'mcap'
  | 'floor'
  | 'progress'
  | 'gainers'
  | 'losers';

export type PhaseFilter = 'all' | 'launching' | 'trading' | 'graduating' | 'graduated';

export type ViewMode = 'grid' | 'list';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'trending', label: 'Trending' },
  { value: 'newest', label: 'Newest' },
  { value: 'volume', label: 'Volume 24h' },
  { value: 'mcap', label: 'Market cap' },
  { value: 'floor', label: 'Floor ratio' },
  { value: 'progress', label: 'Graduation progress' },
  { value: 'gainers', label: 'Top gainers' },
  { value: 'losers', label: 'Top losers' },
];

const PHASE_OPTIONS: { value: PhaseFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'launching', label: 'Launching' },
  { value: 'trading', label: 'Trading' },
  { value: 'graduating', label: 'Graduating' },
  { value: 'graduated', label: 'Graduated' },
];

/** How many tag chips are offered before the list is truncated. */
const TAG_LIMIT = 14;

const VIEW_ITEMS = [
  { value: 'grid' as const, label: <span className="sr-only">Grid view</span>, icon: <Grid size={14} /> },
  { value: 'list' as const, label: <span className="sr-only">List view</span>, icon: <List size={14} /> },
];

/**
 * True while the element's content is wider than its box and there is still
 * scroll left to the right — drives the trailing fade that says "keep going".
 */
function useTrailingFade<T extends HTMLElement>(ref: RefObject<T>, signal: unknown) {
  const [fade, setFade] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setFade(max > 4 && el.scrollLeft < max - 4);
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, ref, signal]);

  return { fade, measure };
}

function TagChip({
  value,
  active,
  onToggle,
}: {
  value: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={cn(
        // min-h-6 keeps the pointer target at the WCAG 2.5.8 24px floor.
        'inline-flex min-h-6 shrink-0 items-center rounded-md border px-2 text-2xs transition-colors duration-200 ease-swift',
        active
          ? 'border-bull/50 bg-bull/15 text-bull'
          : 'border-line bg-elevated/60 text-muted hover:border-line-strong hover:text-fg',
      )}
    >
      {value}
    </button>
  );
}

export interface FilterBarProps {
  query: string;
  onQuery: (query: string) => void;
  sort: SortKey;
  onSort: (sort: SortKey) => void;
  phase: PhaseFilter;
  onPhase: (phase: PhaseFilter) => void;
  tag: string | null;
  onTag: (tag: string | null) => void;
  view: ViewMode;
  onView: (view: ViewMode) => void;
  count: { tokens: number; trading: number };
  className?: string;
}

/**
 * Sticky control strip. Fully controlled — the page owns every value so the
 * spotlight rail's presets can drive it too.
 *
 * Below `sm` the phase, sort and tag controls collapse into a Sheet: inline they
 * cost five stacked rows, which was more than half of a 390px viewport spent on
 * chrome before the first card. The `sm`-and-up layout is unchanged.
 */
export function FilterBar({
  query,
  onQuery,
  sort,
  onSort,
  phase,
  onPhase,
  tag,
  onTag,
  view,
  onView,
  count,
  className,
}: FilterBarProps) {
  const { tokens } = useMarket();
  const [sheetOpen, setSheetOpen] = useState(false);

  const tagRow = useRef<HTMLDivElement>(null);

  const tags = useMemo(() => {
    const seen = new Map<string, number>();
    for (const token of tokens) {
      for (const raw of token.tags) {
        const key = raw.trim();
        if (!key) continue;
        seen.set(key, (seen.get(key) ?? 0) + 1);
      }
    }
    return [...seen.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, TAG_LIMIT)
      .map(([value]) => value);
  }, [tokens]);

  const { fade: tagFade, measure: measureTags } = useTrailingFade(
    tagRow,
    `${tags.length}:${tag ?? ''}`,
  );

  /** Sort is a lens, not a filter, so it does not count towards the badge. */
  const activeFilters = (phase !== 'all' ? 1 : 0) + (tag ? 1 : 0);

  const countLabel = (
    <>
      {compact(count.tokens, 0)} tokens
      <span className="text-subtle"> · </span>
      {compact(count.trading, 0)} trading
    </>
  );

  return (
    <div className={cn('sticky top-header z-30 glass border-b border-line', className)}>
      <div className="shell py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 basis-40 sm:max-w-xs sm:basis-48">
            <Input
              size="sm"
              type="text"
              inputMode="search"
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Search name, symbol or address"
              aria-label="Search tokens"
              prefix={<Search size={15} />}
              suffix={
                query ? (
                  <IconButton
                    label="Clear search"
                    size="sm"
                    variant="ghost"
                    className="-mr-1"
                    onClick={() => onQuery('')}
                  >
                    <X size={14} />
                  </IconButton>
                ) : undefined
              }
            />
          </div>

          {/* ── mobile: everything else lives behind one button ───────────── */}
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 sm:hidden"
            onClick={() => setSheetOpen(true)}
            iconLeft={<Filter size={14} />}
            aria-haspopup="dialog"
          >
            Filters
            {activeFilters > 0 ? (
              <span className="ml-1.5">
                <Badge variant="bull" size="sm">
                  {activeFilters}
                </Badge>
              </span>
            ) : null}
          </Button>

          {/* ── sm and up: inline controls, unchanged ─────────────────────── */}
          <div className="hidden min-w-0 max-w-full overflow-x-auto no-scrollbar sm:block">
            <Segmented<PhaseFilter>
              size="sm"
              className="w-max"
              items={PHASE_OPTIONS}
              value={phase}
              onChange={onPhase}
            />
          </div>

          {/* Select's own className lands on the <select>, so the width lives here. */}
          <div className="hidden w-[10.5rem] shrink-0 sm:block">
            <Select
              size="sm"
              options={SORT_OPTIONS}
              value={sort}
              onChange={(event) => onSort(event.target.value as SortKey)}
              aria-label="Sort tokens"
            />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2.5">
            <p className="hidden font-mono text-2xs text-muted sm:block" aria-live="polite">
              {countLabel}
            </p>
            <Segmented<ViewMode> size="sm" items={VIEW_ITEMS} value={view} onChange={onView} />
          </div>
        </div>

        {tags.length > 0 ? (
          <div
            ref={tagRow}
            onScroll={measureTags}
            className={cn(
              'scroll-inset no-scrollbar mt-2 hidden items-center gap-1.5 pb-0.5 sm:flex',
              // Says "scrollable", instead of leaving the last chip sliced off.
              tagFade && 'mask-fade-r',
            )}
          >
            <span className="shrink-0 text-3xs uppercase tracking-[0.14em] text-subtle">Tags</span>
            {tags.map((value) => (
              <TagChip
                key={value}
                value={value}
                active={tag === value}
                onToggle={() => onTag(tag === value ? null : value)}
              />
            ))}
            {tag ? (
              <button
                type="button"
                onClick={() => onTag(null)}
                className="inline-flex min-h-6 shrink-0 items-center rounded-md px-2 text-2xs text-subtle underline decoration-dotted underline-offset-2 hover:text-fg"
              >
                Clear tag
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} side="bottom" title="Filters">
        <div className="space-y-5">
          <p className="font-mono text-2xs text-muted">{countLabel}</p>

          <div>
            <p className="text-3xs uppercase tracking-[0.14em] text-subtle">Phase</p>
            <div className="scroll-inset no-scrollbar mt-2">
              <Segmented<PhaseFilter>
                className="w-max"
                items={PHASE_OPTIONS}
                value={phase}
                onChange={onPhase}
              />
            </div>
          </div>

          <div>
            <p className="text-3xs uppercase tracking-[0.14em] text-subtle">Sort by</p>
            <div className="mt-2">
              <Select
                options={SORT_OPTIONS}
                value={sort}
                onChange={(event) => onSort(event.target.value as SortKey)}
                aria-label="Sort tokens"
              />
            </div>
          </div>

          {tags.length > 0 ? (
            <div>
              <p className="text-3xs uppercase tracking-[0.14em] text-subtle">Tags</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((value) => (
                  <TagChip
                    key={value}
                    value={value}
                    active={tag === value}
                    onToggle={() => onTag(tag === value ? null : value)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 border-t border-line pt-4">
            <Button
              variant="ghost"
              size="sm"
              disabled={activeFilters === 0}
              onClick={() => {
                onPhase('all');
                onTag(null);
              }}
            >
              Clear all
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="ml-auto"
              onClick={() => setSheetOpen(false)}
            >
              Show {compact(count.tokens, 0)} tokens
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
